import React, { useState, useEffect, useRef, useCallback } from 'react';
import './resumeEditor.css';
import { useParams, useNavigate } from 'react-router-dom';
import { Group, Panel, Separator } from 'react-resizable-panels';
import CodeMirror from '@uiw/react-codemirror';
import { StreamLanguage } from '@codemirror/language';
import { stex } from '@codemirror/legacy-modes/mode/stex';
import { useTheme } from '../../ThemeContext';
import { apiFetch } from '../../api/client';
import { useProfileGuard } from '../../hooks/useProfileGuard';
import { getOrCreateLatexEngine, compileLatex, compileToOnePage, waitForLatexWarmup } from './latexEngine';
import StructuredEditor from './StructuredEditor';
import {
  FiArrowLeft, FiRefreshCw, FiDownload, FiBookmark, FiZap,
  FiChevronUp, FiChevronDown, FiCheckCircle, FiAlertTriangle,
  FiCpu, FiX, FiSend, FiLock, FiCode, FiEdit3, FiEye
} from 'react-icons/fi';

const AUTOSAVE_DELAY = 2000;

function ResumeEditor() {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useProfileGuard();

  const [resume, setResume] = useState(null);
  const [profile, setProfile] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [supportFiles, setSupportFiles] = useState({});
  const [tier, setTier] = useState('free');
  const [latex, setLatex] = useState('');
  const [loadError, setLoadError] = useState(null);

  const [engineStatus, setEngineStatus] = useState('loading');
  const [compiling, setCompiling] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [compileLog, setCompileLog] = useState('');
  const [compileFailed, setCompileFailed] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const [saveState, setSaveState] = useState('idle');
  const [savedToLibrary, setSavedToLibrary] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const [aiEdit, setAiEdit] = useState({ available: false, unlimited: false, perDay: 0, remaining: null });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Editor mode: 'structured' (default), 'latex', 'preview' (mobile-only full preview)
  const [editorMode, setEditorMode] = useState('structured');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const engineRef = useRef(null);
  const latexRef = useRef('');
  const autosaveTimer = useRef(null);
  const pdfUrlRef = useRef(null);
  const compiledOnce = useRef(false);
  const ignoreEmptyEditorChange = useRef(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load resume
  useEffect(() => {
    let cancelled = false;
    setResume(null);
    setProfile(null);
    setSelectedContent(null);
    setSupportFiles({});
    setLatex('');
    latexRef.current = '';
    compiledOnce.current = false;
    ignoreEmptyEditorChange.current = true;
    setPdfUrl(null);
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    (async () => {
      try {
        const data = await apiFetch(`/api/resume/${resumeId}`);
        if (cancelled) return;
        setResume(data.resume);
        setProfile(data.profile || null);
        setSelectedContent(data.resume.selectedContent || null);
        setSupportFiles(data.supportFiles || {});
        setTier(data.tier);
        setAiEdit(data.aiEdit || { available: false, unlimited: false, perDay: 0, remaining: null });
        setSavedToLibrary(data.resume.saved);
        setLatex(data.resume.latexSrc);
        latexRef.current = data.resume.latexSrc;
        compiledOnce.current = false;
        ignoreEmptyEditorChange.current = true;
      } catch (e) {
        if (!cancelled) setLoadError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [resumeId]);

  // Boot WASM engine
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await waitForLatexWarmup();
        const engine = await getOrCreateLatexEngine();
        if (cancelled) return;
        engineRef.current = engine;
        setEngineStatus('ready');
      } catch (e) {
        if (!cancelled) setEngineStatus('failed');
      }
    })();
    return () => {
      cancelled = true;
      engineRef.current = null;
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    };
  }, []);

  const supportFilesRef = useRef({});
  useEffect(() => { supportFilesRef.current = supportFiles; }, [supportFiles]);

  const runCompile = useCallback(async (sourceOverride) => {
    const latexSrc = typeof sourceOverride === 'string' && sourceOverride.trim()
      ? sourceOverride
      : (latexRef.current || latex || resume?.latexSrc || '');
    if (!latexSrc.trim()) return false;

    const compileWithEngine = async (engine) => {
      const result = await compileLatex(engine, latexSrc, { supportFiles: supportFilesRef.current });
      setCompileLog(result.log || '');
      if (result.pdf && result.status === 0) {
        const blob = new Blob([result.pdf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = url;
        setPdfUrl(`${url}#toolbar=0&navpanes=0`);
        setCompileFailed(false);
        return true;
      }
      return false;
    };

    let engine = engineRef.current;
    if (!engine || !engine.isReady()) {
      try {
        engine = await getOrCreateLatexEngine();
        engineRef.current = engine;
        setEngineStatus('ready');
      } catch (e) {
        setEngineStatus('failed');
        setCompileFailed(true);
        setCompileLog(String(e.message || e));
        setLogOpen(true);
        return false;
      }
    }

    setCompiling(true);
    setCompileFailed(false);
    try {
      let ok = await compileWithEngine(engine);
      if (!ok) ok = await compileWithEngine(engine);
      if (!ok) { setCompileFailed(true); setLogOpen(true); }
      return ok;
    } catch (e) {
      setCompileFailed(true);
      setCompileLog(String(e.message || e));
      setLogOpen(true);
      return false;
    } finally {
      setCompiling(false);
    }
  }, [latex, resume, supportFiles]);

  const renderPdf = useCallback((result) => {
    if (!(result?.pdf && result.status === 0)) return false;
    const blob = new Blob([result.pdf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = url;
    setPdfUrl(`${url}#toolbar=0&navpanes=0`);
    setCompileFailed(false);
    return true;
  }, []);

  const fitAndRender = useCallback(async (source) => {
    setCompiling(true);
    setCompileFailed(false);
    try {
      const { source: fitted, result } = await compileToOnePage({
        source,
        supportFiles: supportFilesRef.current
      });
      setCompileLog(result.log || '');
      const ok = renderPdf(result);
      if (!ok) { setCompileFailed(true); setLogOpen(true); return false; }
      if (fitted && fitted !== source) {
        setLatex(fitted);
        latexRef.current = fitted;
        try {
          await apiFetch(`/api/resume/${resumeId}`, { method: 'PUT', body: { latexSrc: fitted } });
        } catch (_) { /* best-effort */ }
      }
      return true;
    } catch (e) {
      setCompileFailed(true);
      setCompileLog(String(e.message || e));
      setLogOpen(true);
      return false;
    } finally {
      setCompiling(false);
    }
  }, [renderPdf, resumeId]);

  // First automatic compile
  useEffect(() => {
    const source = resume?.latexSrc || latex;
    if (engineStatus !== 'ready' || !source?.trim() || compiledOnce.current) return;
    (async () => {
      compiledOnce.current = true;
      const ok = await fitAndRender(source);
      if (!ok) compiledOnce.current = false;
    })();
  }, [engineStatus, resume, latex, fitAndRender]);

  // AI edit
  const runAiEdit = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const data = await apiFetch(`/api/resume/${resumeId}/ai-edit`, {
        method: 'POST',
        body: { prompt }
      });
      setLatex(data.latexSrc);
      latexRef.current = data.latexSrc;
      setAiEdit((prev) => ({ ...prev, remaining: data.unlimited ? null : data.remainingToday }));
      setAiPrompt('');
      setAiOpen(false);
      await fitAndRender(data.latexSrc);
    } catch (e) {
      setAiError(e.data?.upgrade ? e.message : (e.message || 'AI edit failed'));
    } finally {
      setAiBusy(false);
    }
  };

  // Autosave
  const handleLatexChange = useCallback((value) => {
    if (!value && ignoreEmptyEditorChange.current) return;
    if (value) ignoreEmptyEditorChange.current = false;
    setLatex(value);
    latexRef.current = value;
    setSaveState('saving');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        await apiFetch(`/api/resume/${resumeId}`, { method: 'PUT', body: { latexSrc: latexRef.current } });
        setSaveState('saved');
      } catch (e) {
        setSaveState('idle');
      }
    }, AUTOSAVE_DELAY);
  }, [resumeId]);

  useEffect(() => () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); }, []);

  const handleDownload = async () => {
    if (!pdfUrlRef.current || compileFailed) await runCompile();
    if (!pdfUrlRef.current) return;
    const a = document.createElement('a');
    a.href = pdfUrlRef.current;
    const name = (resume && (resume.jobTitle || resume.templateName)) || 'resume';
    a.download = `${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
    a.click();
  };

  const handleSaveToLibrary = async () => {
    setSaveError(null);
    try {
      await apiFetch(`/api/resume/${resumeId}/save`, { method: 'POST' });
      setSavedToLibrary(true);
    } catch (e) { setSaveError(e.message); }
  };

  const handleRegenerate = async () => {
    if (!resume) return;
    setRegenerating(true);
    setSaveError(null);
    try {
      const data = await apiFetch('/api/resume/resume-generate', {
        method: 'POST',
        body: {
          templateId: resume.templateId,
          jobUrl: resume.jobUrl,
          jobTitle: resume.jobTitle,
          company: resume.company
        }
      });
      navigate(`/app/editor/${data.resumeId}`);
    } catch (e) { setSaveError(e.message); }
    finally { setRegenerating(false); }
  };

  if (loadError) {
    return (
      <div className={`editorContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="editorMessage">
          <FiAlertTriangle size={22} />
          <p>{loadError}</p>
          <button className="edPillBtn solid" onClick={() => navigate('/app/resume')}>Back to builder</button>
        </div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className={`editorContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="editorMessage"><span className="edSpinner" /><p>Loading resume…</p></div>
      </div>
    );
  }

  const isFree = tier === 'free';

  return (
    <div className={`editorContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* ===== Toolbar ===== */}
      <div className="editorToolbar">
        <div className="toolbarSection left">
          <button className="edIconBtn" onClick={() => navigate('/app/resume')} title="Back to builder">
            <FiArrowLeft size={16} />
          </button>
          <div className="toolbarTitleGroup">
            <span className="toolbarTitle">{resume.jobTitle || 'Untitled resume'}</span>
            <span className="toolbarSub">
              {resume.templateName}{resume.company ? ` · ${resume.company}` : ''}
            </span>
          </div>
        </div>

        <div className="toolbarSection right">
          <span className={`autosaveTag ${saveState}`}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Draft saved' : ''}
          </span>

          {/* Mode toggle */}
          <div className="editorModeToggle">
            <button
              className={`edModeBtn ${editorMode === 'structured' ? 'active' : ''}`}
              onClick={() => setEditorMode('structured')}
              title="Structured editor"
            >
              <FiEdit3 size={14} />
              {!isMobile && <span>Edit</span>}
            </button>
            <button
              className={`edModeBtn ${editorMode === 'latex' ? 'active' : ''}`}
              onClick={() => setEditorMode('latex')}
              title="LaTeX source"
            >
              <FiCode size={14} />
              {!isMobile && <span>LaTeX</span>}
            </button>
            {isMobile && (
              <button
                className={`edModeBtn ${editorMode === 'preview' ? 'active' : ''}`}
                onClick={() => setEditorMode('preview')}
                title="Preview"
              >
                <FiEye size={14} />
              </button>
            )}
          </div>

          <button className="edPillBtn ghost" onClick={handleRegenerate} disabled={regenerating}>
            <FiZap size={14} /> {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>

          {aiEdit.available ? (
            <button
              className={`edPillBtn ghost ${aiOpen ? 'active' : ''}`}
              onClick={() => { setAiOpen((v) => !v); setAiError(null); }}
              disabled={aiBusy}
              title="Edit with AI prompt"
            >
              <FiCpu size={14} /> {!isMobile && 'AI Edit'}
            </button>
          ) : (
            <div className="saveBtnWrap" data-tooltip="Pro feature — upgrade to use AI edits">
              <button className="edPillBtn ghost" onClick={() => navigate('/pricing')}>
                <FiLock size={13} /> {!isMobile && 'AI Edit'}
              </button>
            </div>
          )}

          <button
            className="edPillBtn ghost"
            onClick={() => runCompile()}
            disabled={compiling || engineStatus !== 'ready'}
          >
            <FiRefreshCw size={14} className={compiling ? 'spinning' : ''} />
            {compiling ? 'Compiling…' : 'Recompile'}
          </button>

          <button className="edPillBtn ghost" onClick={handleDownload} disabled={engineStatus !== 'ready'}>
            <FiDownload size={14} /> {!isMobile && 'PDF'}
          </button>

          <div className="saveBtnWrap" data-tooltip={isFree ? 'Upgrade to save resumes' : undefined}>
            <button
              className={`edPillBtn solid ${savedToLibrary ? 'saved' : ''}`}
              onClick={handleSaveToLibrary}
              disabled={isFree || savedToLibrary}
            >
              {savedToLibrary ? <><FiCheckCircle size={14} /> Saved</> : <><FiBookmark size={14} /> Save</>}
            </button>
          </div>
        </div>
      </div>

      {saveError && <div className="editorErrorBar"><FiAlertTriangle size={13} /> {saveError}</div>}

      {aiEdit.available && aiOpen && (
        <div className="aiEditBar">
          <FiCpu size={15} className="aiEditIcon" />
          <input
            className="aiEditInput"
            type="text"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runAiEdit(); }}
            placeholder='Describe a change… e.g. "Make summary more concise"'
            disabled={aiBusy}
            autoFocus
          />
          <span className="aiEditQuota">
            {aiEdit.unlimited
              ? 'Unlimited'
              : `${aiEdit.remaining ?? aiEdit.perDay}/${aiEdit.perDay} left today`}
          </span>
          <button className="edPillBtn solid aiEditSend" onClick={runAiEdit} disabled={aiBusy || !aiPrompt.trim()}>
            <FiSend size={13} /> {aiBusy ? 'Editing…' : 'Apply'}
          </button>
          <button className="edIconBtn" onClick={() => { setAiOpen(false); setAiError(null); }} title="Close" disabled={aiBusy}>
            <FiX size={16} />
          </button>
        </div>
      )}

      {aiError && <div className="editorErrorBar"><FiAlertTriangle size={13} /> {aiError}</div>}

      {/* ===== Editor body ===== */}
      <div className="editorBody">
        {isMobile ? (
          /* Mobile: single pane based on mode */
          <div className="paneInner" style={{ height: '100%' }}>
            {editorMode === 'preview' ? (
              <div className="paneInner previewPane">
                {renderPreviewContent()}
              </div>
            ) : editorMode === 'latex' ? (
              <CodeMirror
                value={latex}
                height="100%"
                theme={darkMode ? 'dark' : 'light'}
                extensions={[StreamLanguage.define(stex)]}
                onChange={handleLatexChange}
                basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
                style={{ height: '100%', fontSize: '13px' }}
              />
            ) : (
              <div className="structuredEditorPane">
                <StructuredEditor
                  latex={latex}
                  profile={profile}
                  selectedContent={selectedContent}
                  onLatexChange={handleLatexChange}
                />
              </div>
            )}
          </div>
        ) : (
          /* Desktop: split pane */
          <Group orientation="horizontal" style={{ height: '100%' }}>
            <Panel defaultSize="50%" minSize="25%">
              <div className="paneInner">
                {editorMode === 'latex' ? (
                  <CodeMirror
                    value={latex}
                    height="100%"
                    theme={darkMode ? 'dark' : 'light'}
                    extensions={[StreamLanguage.define(stex)]}
                    onChange={handleLatexChange}
                    basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
                    style={{ height: '100%', fontSize: '13px' }}
                  />
                ) : (
                  <div className="structuredEditorPane">
                    <StructuredEditor
                      latex={latex}
                      profile={profile}
                      selectedContent={selectedContent}
                      onLatexChange={handleLatexChange}
                    />
                  </div>
                )}
              </div>
            </Panel>
            <Separator className="resizeHandle" />
            <Panel defaultSize="50%" minSize="25%">
              <div className="paneInner previewPane">
                {renderPreviewContent()}
              </div>
            </Panel>
          </Group>
        )}
      </div>

      {/* ===== Compile log ===== */}
      <div className={`logPanel ${logOpen ? 'open' : ''} ${compileFailed ? 'failed' : ''}`}>
        <button className="logToggle" onClick={() => setLogOpen(!logOpen)}>
          {compileFailed
            ? <><FiAlertTriangle size={13} /> Compile errors</>
            : <><FiCheckCircle size={13} /> Compile log</>}
          {logOpen ? <FiChevronDown size={14} /> : <FiChevronUp size={14} />}
        </button>
        {logOpen && (
          <pre className="logContent">{compileLog || 'No log output yet.'}</pre>
        )}
      </div>
    </div>
  );

  function renderPreviewContent() {
    return (
      <>
        {engineStatus === 'loading' && (
          <div className="previewPlaceholder"><span className="edSpinner" /><p>Loading LaTeX engine…</p></div>
        )}
        {engineStatus === 'failed' && (
          <div className="previewPlaceholder">
            <FiAlertTriangle size={20} />
            <p>Could not load the LaTeX engine. Check your connection and reload.</p>
          </div>
        )}
        {engineStatus === 'ready' && compiling && !pdfUrl && (
          <div className="previewPlaceholder">
            <span className="edSpinner" />
            <p>Compiling… first run may take 30–60s while TeX packages download.</p>
          </div>
        )}
        {engineStatus === 'ready' && !compiling && !pdfUrl && !compileFailed && (
          <div className="previewPlaceholder"><p>Press Recompile to render the PDF</p></div>
        )}
        {engineStatus === 'ready' && !pdfUrl && compileFailed && (
          <div className="previewPlaceholder">
            <FiAlertTriangle size={20} />
            <p>Compilation failed — check the log below and fix the LaTeX.</p>
          </div>
        )}
        {pdfUrl && (
          <iframe title="PDF preview" src={pdfUrl} className="pdfFrame" />
        )}
        {compiling && pdfUrl && <div className="compileOverlay"><span className="edSpinner" /></div>}
      </>
    );
  }
}

export default ResumeEditor;
